import type { ChangeEvent } from 'react';
import { useState, useCallback, useEffect, useRef } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import type { CallOptions, Options } from 'use-debounce';

// Support both input and textarea elements for maximum flexibility
type InputElement = HTMLInputElement | HTMLTextAreaElement;

type UseDebouncedInputReturn<T = string> = {
  value: T; // The current input value (updates immediately)
  debouncedValue: T; // The debounced value (updates after delay)
  onChange: (e: ChangeEvent<InputElement>) => void; // Event handler for input changes
  cancel: () => void; // Cancel pending debounced update
  flush: () => void; // Immediately execute pending debounced update
  isPending: () => boolean; // Check if debounced update is pending
  reset: (newValue?: T) => void; // Reset both values and cancel pending updates
};

/**
 * A custom hook that provides debounced onChange functionality for input components.
 *
 * This hook maintains two separate state values:
 * - `value`: Updates immediately on every keystroke for responsive UI
 * - `debouncedValue`: Updates only after the specified delay for expensive operations
 *
 * This pattern is ideal for search inputs, API calls, or any scenario where you need
 * immediate UI feedback but want to limit the frequency of expensive operations.
 *
 * @param initialValue - The initial input value (default: '')
 * @param delay - The debounce delay in milliseconds (default: 300)
 * @param options - Additional debounce options (leading, trailing, maxWait, etc.)
 * @returns An object containing value states, onChange handler, and control functions
 *
 * @example
 * ```tsx
 * import { useDebouncedInput } from './hooks/useDebouncedInput';
 * import { Input } from '@/components/ui/input';
 *
 * function SearchComponent() {
 *   const { value, debouncedValue, onChange, reset } = useDebouncedInput('', 500);
 *
 *   // Use debouncedValue for expensive operations like API calls
 *   useEffect(() => {
 *     if (debouncedValue) {
 *       searchAPI(debouncedValue);
 *     }
 *   }, [debouncedValue]);
 *
 *   return (
 *     <div>
 *       <Input
 *         value={value}        // Immediate UI updates
 *         onChange={onChange}  // Handle all input changes
 *         placeholder="Type to search..."
 *       />
 *       <button onClick={() => reset()}>Clear</button>
 *       <p>Searching for: {debouncedValue}</p>
 *     </div>
 *   );
 * }
 * ```
 */
export function useDebouncedInput<T = string>(
  initialValue: T = '' as T,
  delay: number = 300,
  options?: Omit<Options, 'leading'> & Pick<CallOptions, 'leading'>,
): UseDebouncedInputReturn<T> {
  // Immediate state - updates on every keystroke for responsive UI
  const [value, setValue] = useState<T>(initialValue);

  // Debounced state - updates only after delay period for expensive operations
  const [debouncedValue, setDebouncedValue] = useState<T>(initialValue);

  // Store initial value in ref to prevent unnecessary re-renders and provide stable reset target
  const initialValueRef = useRef(initialValue);

  // Create debounced callback that will update the debounced value after the specified delay
  // This callback is memoized by useDebouncedCallback to prevent unnecessary re-creations
  const debouncedSetValue = useDebouncedCallback(
    (newValue: T) => {
      setDebouncedValue(newValue);
    },
    delay,
    options,
  );

  // Cancel any pending debounced updates when delay or options change
  // This ensures the debounced behavior respects the latest configuration
  useEffect(() => {
    debouncedSetValue.cancel();
  }, [delay, options, debouncedSetValue]);

  // Input change handler that updates both immediate and debounced values
  // The immediate value updates instantly, while the debounced value updates after delay
  const handleChange = useCallback(
    (e: ChangeEvent<InputElement>) => {
      const newValue = e.target.value as T;
      setValue(newValue); // Immediate UI update
      debouncedSetValue(newValue); // Debounced update for expensive operations
    },
    [debouncedSetValue],
  );

  // Reset function that clears both values and cancels any pending updates
  // Optionally accepts a new value, otherwise resets to initial value
  const reset = useCallback(
    (newValue?: T) => {
      const resetValue = newValue === undefined ? initialValueRef.current : newValue;
      setValue(resetValue);
      setDebouncedValue(resetValue);
      debouncedSetValue.cancel(); // Cancel any pending debounced update
    },
    [debouncedSetValue],
  );

  // Control functions that provide direct access to debounced callback methods
  // These are direct references to avoid unnecessary function wrapping
  const cancel = debouncedSetValue.cancel; // Cancel pending debounced update
  const flush = debouncedSetValue.flush; // Immediately execute pending update
  const isPending = debouncedSetValue.isPending; // Check if update is pending

  return {
    value,
    debouncedValue,
    onChange: handleChange,
    cancel,
    flush,
    isPending,
    reset,
  };
}
